import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs';
import { LucideAngularModule, CheckCircle2 } from 'lucide-angular';
import { LessonPlanService } from './services/lesson-plan.service';
import { THEME_COLOR } from './services/lesson-plan.models';
import { StepInputComponent } from './steps/step-input/step-input';
import { StepSettingsComponent } from './steps/step-settings/step-settings';
import { StepTopicComponent } from './steps/step-topic/step-topic';
import { StepObjectivesComponent } from './steps/step-objectives/step-objectives';
import { StepStructureComponent } from './steps/step-structure/step-structure';
import { StepContentComponent } from './steps/step-content/step-content';
import { AmplitudeService } from '../../../common/analytics/amplitude.service';

interface WizardStep {
  num: number;
  title: string;
}

@Component({
  selector: 'app-lesson-plan',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    StepInputComponent,
    StepSettingsComponent,
    StepTopicComponent,
    StepObjectivesComponent,
    StepStructureComponent,
    StepContentComponent,
    RouterOutlet,
  ],
  templateUrl: './lesson-plan.html',
  styleUrl: './lesson-plan.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LessonPlanComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly lessonPlanService = inject(LessonPlanService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly amplitudeService = inject(AmplitudeService);

  /** Track if we've already handled the initial route to prevent duplicate events */
  private hasTrackedInitialRoute = false;

  protected readonly themeColor = THEME_COLOR;
  protected readonly currentStep$ = this.lessonPlanService.currentStep$;
  protected readonly isLoading$ = this.lessonPlanService.isLoading$;
  protected readonly error$ = this.lessonPlanService.error$;
  protected readonly maxAllowedStep$ = this.lessonPlanService.maxAllowedStep$;

  protected readonly icons = {
    CheckCircle2,
  };

  protected readonly steps: WizardStep[] = [
    { num: 1, title: 'Basics' },
    { num: 2, title: 'Settings' },
    { num: 3, title: 'Topic' },
    { num: 4, title: 'Objectives' },
    { num: 5, title: 'Structure' },
    { num: 6, title: 'Present' },
  ];

  ngOnInit(): void {
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const id = params.get('id');
        if (id) {
          // Reset the tracking flag when loading an existing lesson plan
          // This ensures that if user navigates to create a new one later, events will be tracked
          this.hasTrackedInitialRoute = false;

          // Check if we already have this lesson plan loaded in memory
          // (happens when navigating after creating in step 1)
          if (this.lessonPlanService.lessonPlanId === id) {
            // Already loaded, no need to fetch again
            return;
          }

          // Load existing lesson plan from backend
          this.lessonPlanService
            .loadLessonPlan(id)
            .pipe(take(1))
            .subscribe((lessonPlan) => {
              if (!lessonPlan) {
                // Lesson plan not found, redirect to create new
                this.router.navigate(['/lesson-plan']);
              }
            });
        } else {
        // Creating new lesson plan - always reset state
        // After step 1, the URL always contains the lesson plan ID,
        // so navigating to /lesson-plan without an ID means creating a new one
        // Use flag to prevent duplicate tracking from route subscription firing multiple times
        if (!this.hasTrackedInitialRoute) {
          this.hasTrackedInitialRoute = true;
          this.amplitudeService.trackEvent('lesson_plan_creation_started');
          this.lessonPlanService.reset();
          this.amplitudeService.trackEvent('lesson_plan_step_basics_viewed');
        } else {
          // Route fired again but we've already tracked - just reset without tracking
          this.lessonPlanService.reset();
        }
      }
      });

    // Subscribe to step changes and navigate to child routes when on step 6 (Present)
    this.currentStep$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((step) => {
        if (step === 6) {
          // Check if we're already on a child route
          const currentUrl = this.router.url;
          if (!currentUrl.includes('/presenter') &&
              !currentUrl.includes('/structure') &&
              !currentUrl.includes('/assignments') &&
              !currentUrl.includes('/admin-plan')) {
            // Navigate to default child route (presenter)
            this.router.navigate(['presenter'], { relativeTo: this.route });
          }
        }
      });
  }

  protected getProgressWidth(currentStep: number): string {
    const segments = currentStep - 1;
    if (segments === 0) return '0';

    // Each segment = (100% - 2.5rem) / 5, subtract 0.5rem to stop before circle center
    return `calc(${segments} * (100% - 2.5rem) / 5 - 0.5rem)`;
  }

  protected getMobileProgressWidth(currentStep: number): string {
    return `${(currentStep / 6) * 100}%`;
  }

  protected getStepTitle(currentStep: number): string {
    return this.steps[currentStep - 1]?.title || '';
  }

  protected onStepNext(): void {
    this.lessonPlanService.nextStep();
    this.scrollToTop();
  }

  protected onStepBack(): void {
    this.lessonPlanService.prevStep();
    this.scrollToTop();
  }

  /**
   * Scroll to top with Safari mobile repaint fix to prevent white screen issues.
   */
  private scrollToTop(): void {
    // Use instant scroll on mobile Safari to avoid rendering issues
    const isSafariMobile =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.userAgent.includes('Safari') && 'ontouchstart' in window);

    window.scrollTo({
      top: 0,
      behavior: isSafariMobile ? 'instant' : 'smooth',
    });

    // Force repaint on Safari to prevent white screen bug
    if (isSafariMobile) {
      requestAnimationFrame(() => {
        document.body.style.transform = 'translateZ(0)';
        requestAnimationFrame(() => {
          document.body.style.transform = '';
        });
      });
    }
  }

  protected goToDashboard(): void {
    this.router.navigate(['/dashboard']);
  }
}
